import type { RDKitModule } from "@rdkit/rdkit";
import initRDKit from "@/utils/initRDKit";

/**
 * SMILES 에서 Exact Mass · InChIKey 를 계산한다 (S4 · 2026-08-18).
 *
 * 기획: `docs/2026/260818_표준물질DB화면_기획.md` §3-9 · §4.2.
 *
 * 원본 프로그램(NIST MS Search)의 히트 상세에는 `Exact Mass` 와 `InChIKey` 가
 * 있다. 우리 참조 DB 에는 없지만 SMILES 가 있으므로 계산할 수 있다.
 * `NIST#` 는 NIST 식별자라 **제외**한다(§3-9).
 *
 * 계산은 이미 화면에 있는 RDKit WASM 으로 한다 — 백엔드에 새 의존성을
 * 들이지 않는다.
 *
 * **이 값들은 측정값이 아니라 SMILES 에서 유도한 계산값이다.** 화면은 반드시
 * 그렇게 표기해야 한다. 계산값을 실측처럼 보이게 두면 근거가 뒤바뀐다.
 */

export interface MolecularIdentifiers {
  /** 홑동위원소 질량(monoisotopic). SMILES 에서 계산. */
  exactMass: number | null;
  /** InChIKey (27자). SMILES 에서 계산. */
  inchiKey: string | null;
  /** 계산하지 못한 이유. 값이 없을 때만 채워진다. */
  error: string | null;
}

const EMPTY: MolecularIdentifiers = {
  exactMass: null,
  inchiKey: null,
  error: null,
};

/**
 * 이미 로드된 RDKit 모듈로 계산한다.
 *
 * 모듈을 인자로 받아 테스트가 WASM 없이도 계약을 고정할 수 있게 한다.
 * 실패해도 **던지지 않고** 이유를 담아 돌려준다 — 상세 화면 전체가
 * 식별자 하나 때문에 무너지면 안 된다.
 */
export function deriveIdentifiers(
  rdkit: RDKitModule,
  smiles: string,
): MolecularIdentifiers {
  if (!smiles?.trim()) {
    return { ...EMPTY, error: "SMILES 미보유" };
  }

  let mol: ReturnType<RDKitModule["get_mol"]> | null = null;
  try {
    mol = rdkit.get_mol(smiles);
    if (!mol) {
      return { ...EMPTY, error: "SMILES 해석 실패" };
    }

    let exactMass: number | null = null;
    try {
      const descriptors = JSON.parse(mol.get_descriptors()) as {
        exactmw?: number;
      };
      exactMass =
        typeof descriptors.exactmw === "number" &&
        Number.isFinite(descriptors.exactmw)
          ? descriptors.exactmw
          : null;
    } catch {
      exactMass = null;
    }

    let inchiKey: string | null = null;
    try {
      const inchi = mol.get_inchi();
      const key = inchi ? rdkit.get_inchikey_for_inchi(inchi) : "";
      inchiKey = key || null;
    } catch {
      inchiKey = null;
    }

    if (exactMass === null && inchiKey === null) {
      return { ...EMPTY, error: "SMILES 해석 실패" };
    }
    return { exactMass, inchiKey, error: null };
  } catch {
    return { ...EMPTY, error: "SMILES 해석 실패" };
  } finally {
    // WASM 힙 누수 방지 — 상세 모달은 화합물마다 다시 계산한다.
    mol?.delete();
  }
}

/** RDKit 을 로드해 계산한다. 로드 실패도 예외 대신 사유로 돌려준다. */
export async function computeMolecularIdentifiers(
  smiles: string,
): Promise<MolecularIdentifiers> {
  if (!smiles?.trim()) {
    return { ...EMPTY, error: "SMILES 미보유" };
  }
  try {
    const rdkit = await initRDKit();
    return deriveIdentifiers(rdkit, smiles);
  } catch {
    return { ...EMPTY, error: "구조 계산 모듈을 불러오지 못했습니다" };
  }
}
